import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { playStartChime, playTick } from "../lib/chatSounds";
import { toast } from "sonner";

/**
 * Owns everything billing-clock-related for the customer ChatPage:
 *   • Polls /billing on mount, then heartbeats /billing/heartbeat every 15s
 *     so the backend settles elapsed-time charges in real time.
 *   • Tracks a 1-second `tick` counter so callers can re-render their timer
 *     without each one running its own interval.
 *   • Plays the warm two-tone start chime once per chat session.
 *   • Plays the short countdown tick at each whole second between 5..1s
 *     remaining (final tick is louder for "time's up").
 *   • Detects "user opened a chat with zero affordable time" and surfaces
 *     a suggested top-up amount so the parent can open the wallet modal.
 *   • Detects backend `auto_ended` and invokes the parent callback so the
 *     parent can tear down the ongoing-chat dock, refresh the wallet, etc.
 *
 * @param chatId        the active chat's id
 * @param chat          the chat document (used for status === "ended" gating)
 * @param astroRatePerMin fallback rate when billing state hasn't arrived yet
 * @param options
 *        - onAutoEnd():  fired when backend reports auto-end. Caller should
 *          clear the active chat & refresh wallet.
 * @returns {
 *   billing, setBilling, tick,
 *   lowBalancePromptAmount,         // non-null = parent should open top-up
 *   clearLowBalancePrompt,
 * }
 */
export function useChatBilling(chatId, chat, astroRatePerMin, { onAutoEnd } = {}) {
  const [billing, setBilling] = useState(null);
  const [tick, setTick] = useState(0);
  const [lowBalancePromptAmount, setLowBalancePromptAmount] = useState(null);

  const startChimePlayedRef = useRef(false);
  const lastTickSecondRef = useRef(null);
  const lowBalancePromptFiredRef = useRef(false);
  const lowBalancePromptTimerRef = useRef(null);

  // Start chime — once per chat mount, ~250ms after the chat object loads.
  useEffect(() => {
    if (!chat || chat.status === "ended") return;
    if (startChimePlayedRef.current) return;
    startChimePlayedRef.current = true;
    const t = setTimeout(() => { try { playStartChime(); } catch (_) {} }, 250);
    return () => clearTimeout(t);
  }, [chat?.chat_id, chat?.status]);

  // Countdown tick — fires once per second-bucket between 5..1.
  useEffect(() => {
    if (!billing || chat?.status === "ended") return;
    const heartbeatTs = billing._heartbeat_ts || Date.now();
    const secsSinceHeartbeat = Math.max(0, Math.floor((Date.now() - heartbeatTs) / 1000));
    const secsLeft = Math.max(0, (billing.seconds_left || 0) - secsSinceHeartbeat);
    if (secsLeft >= 1 && secsLeft <= 5) {
      if (lastTickSecondRef.current !== secsLeft) {
        lastTickSecondRef.current = secsLeft;
        try { playTick(secsLeft === 1); } catch (_) {}
      }
    } else {
      lastTickSecondRef.current = null;
    }
  }, [tick, billing, chat?.status]);

  // 1-second tick interval so the header timer stays live.
  useEffect(() => {
    if (!chat || chat.status === "ended") return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [chat]);

  // Heartbeat polling — GET on mount, POST every 15s thereafter.
  // Stops once the chat is "ended" so the wallet timer doesn't keep
  // refreshing _heartbeat_ts on the client (which would visually drain
  // seconds_left in any display that subtracts secsSinceHeartbeat).
  useEffect(() => {
    if (!chatId) return;
    if (chat?.status === "ended") return;
    let cancelled = false;
    const heartbeat = async (settle) => {
      try {
        const url = settle
          ? `/chats/${chatId}/billing/heartbeat`
          : `/chats/${chatId}/billing`;
        const { data } = settle ? await api.post(url) : await api.get(url);
        if (cancelled) return;
        setBilling({ ...data, _heartbeat_ts: Date.now() });
        if (data.auto_ended) {
          toast("Your balance is over — chat has ended.", { icon: "⏱️", duration: 7000 });
          setLowBalancePromptAmount(100);
          onAutoEnd?.();
        }
      } catch (_) { /* ignore */ }
    };
    heartbeat(false);
    const id = setInterval(() => { heartbeat(true); }, 15000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, chat?.status]);

  // Soft top-up prompt — if the user opens a chat with zero free time AND
  // zero affordable wallet time, give them ~2.5s to read the greeting then
  // surface the recharge modal. Fires at most once per chat mount.
  useEffect(() => {
    if (!chat) return;
    if (!billing || billing.seconds_left === undefined) return;
    if (lowBalancePromptFiredRef.current) return;
    const noFree = (billing.free_minutes_remaining ?? 0) === 0;
    const noTime = (billing.seconds_left ?? 0) <= 0;
    if (noFree && noTime) {
      lowBalancePromptFiredRef.current = true;
      const rate = billing.rate_per_min || astroRatePerMin || 0;
      const suggested = Math.max(99, Math.ceil((rate * 5) / 50) * 50);
      lowBalancePromptTimerRef.current = setTimeout(() => {
        setLowBalancePromptAmount(suggested);
      }, 2500);
    }
  }, [chat, billing, astroRatePerMin]);

  // Cancel a pending low-balance prompt timer on unmount.
  useEffect(() => () => {
    if (lowBalancePromptTimerRef.current) {
      clearTimeout(lowBalancePromptTimerRef.current);
    }
  }, []);

  const clearLowBalancePrompt = () => setLowBalancePromptAmount(null);

  return {
    billing,
    setBilling,
    tick,
    lowBalancePromptAmount,
    clearLowBalancePrompt,
  };
}
